import * as React from 'react';
import { Switch, generatePath } from 'react-router-dom';
import { Route, Redirect } from 'platform/react-router';

import { GenericErrorPage } from 'shared/routes/errors/GenericErrorPage';
import { DisclosuresWizard } from 'sellDirect/components/closingDash/disclosures/DisclosuresWizard';
import { ContractWizard } from 'sellDirect/components/closingDash/contract/ContractWizard';
import { ServiceProvidersWizard } from 'sellDirect/components/closingDash/serviceProviders/ServiceProvidersWizard';
import { getHubSellingRoute, getHubLateCheckoutWizardRoute } from 'shared/helpers/routes';
import { LateCheckoutWizard } from 'sellDirect/components/closingDash/lateCheckout/LateCheckoutWizard';
import { ClosingWizard } from 'sellDirect/components/closingDash/closing/ClosingWizard';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { useObservability } from 'shared/helpers/observability';
import { ChangeCloseDateWizard } from 'sellDirect/components/closingDash/changeCloseDate/ChangeCloseDateWizard';
import { GeneralAddendumWizard } from 'sellDirect/components/closingDash/generalAddendums/GeneralAddendumWizard';
import { ClosedOfferPage } from 'sellDirect/routes/sell/ClosedOfferPage';
import { FinalizedOfferPage } from 'sellDirect/routes/sell/FinalizedOfferPage';
import { UnderContractOfferPage } from 'sellDirect/routes/sell/UnderContractOfferPage';
import { WithdrawnOfferPage } from 'sellDirect/routes/sell/WithdrawnOfferPage';
import { getClosingOrClosedOffer, useOfferContext } from 'sellDirect/OfferContext';
import { useQualificationContext } from 'shared/helpers/QualificationContext';

const getDashboardPath = (page = ':page'): string => {
  return `${getHubSellingRoute()}/${page}`;
};

/** Legacy closing checkout route. */
export const getClosingCheckoutPage = () => getDashboardPath('closing');
/** Legacy closing summary route. */
export const getClosingSummaryRoute = () => getDashboardPath('summary');
/** Legacy closing contract setup & signing route. */
export const getClosingContractRoute = () => getDashboardPath('contract');
/** Legacy closing service providers task route. */
export const getClosingServiceProvidersRoute = () => getDashboardPath('service-providers');
/** Legacy closing disclosures task route. */
export const getClosingDisclosuresRoute = () => getDashboardPath('disclosures');

/** New route for handling changing the close date */
export const getClosingChangeCloseDateRoute = () => getDashboardPath('change-close-date');
/** New route for handling general addenda */
export const getClosingGeneralAddenda = () => getDashboardPath('general-addendum');

/** Closing experience routes. */
export const SellDirectClosingRouter = () => {
  const { trackError } = useObservability();
  const { addressUUID } = useQualificationContext();
  const { loading: offerLoading, offer, experimentationData } = useOfferContext();
  const closingOrClosedOffer = getClosingOrClosedOffer(offer);
  if (!closingOrClosedOffer || !closingOrClosedOffer.refetchWebLead) {
    trackError('missing-closing-page-data', undefined, { offerId: offer?.id });
    return <GenericErrorPage from="SellDirectClosingRouter" />;
  }

  if (!closingOrClosedOffer) {
    trackError('missing-closing-page-data', undefined, { offerId: offer?.id });
    return <GenericErrorPage from="SellDirectClosingRouter" />;
  }

  const tasks = closingOrClosedOffer.closingTasks || ([] as ClosingTasks);

  const postContractTaskRoutes =
    closingOrClosedOffer.state === 'UnderContract' || closingOrClosedOffer.state === 'Closed'
      ? [
          <Route exact key="closing-page-route" path={getClosingCheckoutPage()}>
            <ClosingWizard offer={closingOrClosedOffer} closingTasks={tasks} />
          </Route>,
          <Route exact key="general-addenda-route" path={getClosingGeneralAddenda()}>
            <GeneralAddendumWizard offer={closingOrClosedOffer} closingTasks={tasks} />
          </Route>,
          <Route exact key="change-close-date-wizard-route" path={getClosingChangeCloseDateRoute()}>
            <ChangeCloseDateWizard offer={closingOrClosedOffer} closingTasks={tasks} />
          </Route>,
          <Route exact key="late-checkout-wizard-route" path={getHubLateCheckoutWizardRoute()}>
            <LateCheckoutWizard offer={closingOrClosedOffer} closingTasks={tasks} />
          </Route>,
        ]
      : [];

  const routes = [];
  if (closingOrClosedOffer.state === 'Finalized') {
    if (closingOrClosedOffer.isPurchaseAgreementSigned) {
      routes.push(
        <Route exact key="selling-route" path={getHubSellingRoute()}>
          <UnderContractOfferPage
            isLoading={offerLoading}
            offer={closingOrClosedOffer}
            closingTasks={tasks}
          />
        </Route>,
      );
    } else {
      routes.push(
        <Route exact key="selling-route" path={getHubSellingRoute()}>
          <FinalizedOfferPage offer={closingOrClosedOffer} />
        </Route>,
      );
    }
    if (!closingOrClosedOffer.isExpired) {
      routes.push(
        <Route exact key="contract-page-route" path={getClosingContractRoute()}>
          <ContractWizard offer={closingOrClosedOffer} closingTasks={tasks} />
        </Route>,
      );
    }
  } else if (closingOrClosedOffer.state === 'UnderContract') {
    routes.push(
      <Route exact key="selling-route" path={getHubSellingRoute()}>
        <UnderContractOfferPage
          isLoading={offerLoading}
          offer={closingOrClosedOffer}
          closingTasks={tasks}
        />
      </Route>,
    );
    routes.push(
      <Route exact key="service-providers-page-route" path={getClosingServiceProvidersRoute()}>
        <ServiceProvidersWizard offer={closingOrClosedOffer} closingTasks={tasks} />
      </Route>,
    );
    routes.push(
      <Route exact key="disclosures-page-route" path={getClosingDisclosuresRoute()}>
        <DisclosuresWizard
          offer={closingOrClosedOffer}
          closingTasks={tasks}
          isEnhancedDisclosureFlowEnabled={experimentationData.isEnhancedDisclosureFlowEnabled}
        />
      </Route>,
    );
    routes.push(...postContractTaskRoutes);
  } else if (closingOrClosedOffer.state === 'Withdrawn') {
    routes.push(
      <Route exact key="selling-route" path={getHubSellingRoute()}>
        <WithdrawnOfferPage
          isLoading={offerLoading}
          offer={closingOrClosedOffer}
          closingTasks={tasks}
        />
      </Route>,
    );
  } else if (closingOrClosedOffer.state === 'Closed') {
    routes.push(
      <Route exact key="selling-route" path={getHubSellingRoute()}>
        <ClosedOfferPage
          isLoading={offerLoading}
          offer={closingOrClosedOffer}
          closingTasks={tasks}
          experimentationData={experimentationData}
        />
      </Route>,
    );
    routes.push(...postContractTaskRoutes);
  }

  // Last route is redirect so if none of the above routes match, we will redirect to /sell
  routes.push(
    <Route
      key="redirect-route"
      render={() => <Redirect to={generatePath(getHubSellingRoute(), { addressUUID })} />}
    />,
  );

  return <Switch>{routes}</Switch>;
};
